import { Helmet } from 'react-helmet-async';
import React from 'react';
import { FormControl } from 'react-bootstrap';
import { getOrgTermsContent } from '../../../content/utils.js';
import InitiativeHeader from '../../headers/PageHeaders/Initiative/InitiativeHeader.jsx';
import { EditInitiativeLink } from '../../links/index.jsx';
import InitiativeParticipationsHeader from './InitiativeParticipationsHeader.jsx';
import InitiativeParticipationsTable from './InitiativeParticipationsTable/index.jsx';
import InitiativeParticipationsTree from './InitiativeParticipationsTree.jsx';
import InitiativeUploadStatus from './InitiativeUploadStatus.jsx';
import SubHeader from '../../SubHeader.jsx';
import StakeholdersUploadStatus from './StakeholdersUploadStatus.jsx';
import { useFetchFile } from '../../../hooks/useFetchFile.js';
import StakeholdersParticipations from './StakeholdersParticipations.jsx';
import StakeholdersParticipationHeader from './StakeholdersParticipationHeader.jsx';
import { flattenedTree } from '../../../lib/utils.js';
import styles from './index.module.css';

const csvParcingMsg =
  'Preview is not available due to the attached CSV file. To view the actual structure, please save the initiative.';

const InitiativeDetail = (props) => {
  const {
    initiative,
    isPreview,
    initiativeParticipationId,
    stakeholderParticipationId,
    initiativeParticipation,
    initiativeParticipations,
    initiativeStakeholders,
    ancestryInitiativeParticipations,
    handleSearchStakeholders,
    organization,
    handleSearch,
    searchQuery,
    status,
    sentiment,
    apiUrl,
    participantsCsv,
    stakeholdersCsv,
    portfolio,
  } = props;

  const { downloadBlob } = useFetchFile();

  const { title } = initiative;

  const onDownloadStakeholderErrorsCsv = () => {
    downloadBlob(
      apiUrl,
      `${initiative.type}_initiatives/${initiative.id}/stakeholder_structure_errors_csv`,
      null,
      'errors-organization-structure CSV',
      'csv'
    );
  };

  const onDownloadGroupErrorsCsv = () => {
    downloadBlob(
      apiUrl,
      `${initiative.type}_initiatives/${initiative.id}/group_structure_errors_csv`,
      null,
      'errors-group-structure CSV',
      'csv'
    );
  };

  const renderInitiativeParticipations = () => {
    if (participantsCsv && isPreview) {
      return (
        <React.Fragment>
          <SubHeader small>
            <h2>Participating Groups</h2>
          </SubHeader>

          <div className="component component-bg-white">{csvParcingMsg}</div>
        </React.Fragment>
      );
    }

    const resultCount = () => {
      return (
        flattenedTree(
          initiativeParticipations.filter(
            (i) => i.participantType === 'Employee'
          )
        ).length +
        initiativeParticipations.filter((i) => i.participantType === 'Group')
          .length
      );
    };

    return (
      <React.Fragment>
        <InitiativeParticipationsHeader
          initiative={initiative}
          resultsCount={resultCount()}
          initiativeParticipation={initiativeParticipation}
          handleSearch={handleSearch}
          searchQuery={searchQuery}
        />
        <div className={styles.initiativeParticipations}>
          <div
            className={`${styles.initiativeParticipationTree} component component-bg-white component-border-right component-border-bottom`}
          >
            <InitiativeParticipationsTree
              isPreview={isPreview}
              initiative={initiative}
              initiativeParticipations={ancestryInitiativeParticipations}
              activeInitiativeParticipationId={initiativeParticipationId}
              organization={organization}
              status={status}
              sentiment={sentiment}
            />
          </div>
          <div className="flex-1 component-bg-white component-border-bottom">
            <div className="component component-sm flex flex-justify flex-1">
              <h4>
                {initiativeParticipation
                  ? initiativeParticipation.label
                  : organization.name}
              </h4>
              <div style={{ width: '100%', maxWidth: '500px' }}>
                <FormControl
                  placeholder="Search"
                  type="text"
                  className="search-icon m-r-30"
                  onChange={handleSearch}
                  value={searchQuery}
                />
              </div>
            </div>
            <InitiativeParticipationsTable
              isPreview={isPreview}
              initiative={initiative}
              initiativeParticipations={initiativeParticipations}
            />
          </div>
        </div>
      </React.Fragment>
    );
  };

  const renderStakeholdersParticipations = () => {
    if (stakeholdersCsv) {
      return (
        <div className="component component-bg-white">
          Preview is not available due to the attached CSV file. To view the
          actual structure, please save the initiative.
        </div>
      );
    }
    return initiative.stakeholderParticipationCount > 0 ? (
      <StakeholdersParticipations
        isPreview={isPreview}
        initiative={initiative}
        organization={organization}
        stakeholdersParticipations={initiativeStakeholders}
        activeStakeholderParticipationId={stakeholderParticipationId}
        status={status}
      />
    ) : (
      <div className="component-xl component-bg-white component-border-bottom text-center">
        <h2 className="text-info-brand m-b-16">
          There are no participating stakeholders in the{' '}
          {getOrgTermsContent('initiative')}.
        </h2>
        <p>
          You can upload a CSV file or add participants manually using the{' '}
          {initiative.project.archived ? (
            <a
              onClick={() =>
                window.alert(getOrgTermsContent('archivedProjectMessage'))
              }
            >
              Upload a CSV
            </a>
          ) : initiative.draft ? (
            <span>{getOrgTermsContent('initiative')} edit page.</span>
          ) : (
            <EditInitiativeLink initiative={initiative}>
              <span className="text-info-brand">
                {getOrgTermsContent('initiative')} edit page.
              </span>
            </EditInitiativeLink>
          )}
        </p>
      </div>
    );
  };
  return (
    <React.Fragment>
      <Helmet>
        <title>{title} | ChangeSync</title>
      </Helmet>
      {!isPreview ? (
        <InitiativeHeader portfolio={portfolio} initiative={initiative} />
      ) : null}
      {!isPreview && (
        <InitiativeUploadStatus
          initiative={initiative}
          onDownloadErrorsCsv={onDownloadGroupErrorsCsv}
        />
      )}
      {!initiative.groupsData &&
      (!initiative.initiativeUpload ||
        InitiativeUploadStatus.completed(initiative)) ? (
        <React.Fragment>
          <SubHeader flexJustify small>
            <div className="flex flex-1">
              <h2>Participating Groups</h2>
              <strong className="header-item bordered text-muted">
                Current Level&nbsp;:&nbsp;
                <span className="text-primary">0 Result</span>
              </strong>
            </div>
          </SubHeader>
          <div className="component-xl component-bg-white component-border-bottom text-center">
            <h2 className="text-info-brand m-b-16">
              There are no participating groups in the{' '}
              {getOrgTermsContent('initiative')}.
            </h2>

            <p>
              You can upload a CSV file or add participants manually using the{' '}
              {initiative.project.archived ? (
                <a
                  onClick={() =>
                    window.alert(
                      'Archived project \n\nIt’s not allowed to take any action on archived projects. If you want to proceed, please unarchive the project first.'
                    )
                  }
                >
                  Upload a CSV
                </a>
              ) : initiative.draft ? (
                <span>{getOrgTermsContent('initiative')} edit page.</span>
              ) : (
                <EditInitiativeLink initiative={initiative}>
                  <span className="text-info-brand">
                    {getOrgTermsContent('initiative')} edit page.
                  </span>
                </EditInitiativeLink>
              )}
            </p>
          </div>
        </React.Fragment>
      ) : null}
      {initiative.groupsData ? (
        InitiativeUploadStatus.completed(initiative) ||
        isPreview ||
        initiative.groupsData.length ? (
          renderInitiativeParticipations()
        ) : (
          <InitiativeParticipationsHeader
            initiative={initiative}
            resultsCount={initiativeParticipations.length}
            initiativeParticipation={initiativeParticipation}
            handleSearch={handleSearch}
            searchQuery={searchQuery}
          />
        )
      ) : null}

      <StakeholdersUploadStatus
        initiative={initiative}
        onDownloadErrorsCsv={onDownloadStakeholderErrorsCsv}
      />
      {!stakeholdersCsv && (
        <StakeholdersParticipationHeader
          initiative={initiative}
          stakeholdersCsv={stakeholdersCsv}
          initiativeStakeholders={initiativeStakeholders}
          stakeholdersParticipations={initiativeStakeholders}
          activeStakeholderParticipationId={stakeholderParticipationId}
          handleSearchStakeholders={handleSearchStakeholders}
        />
      )}
      {renderStakeholdersParticipations(initiativeStakeholders)}
    </React.Fragment>
  );
};

export default InitiativeDetail;
